import { ChangeDetectionStrategy, Component, booleanAttribute, computed, effect, inject, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { TranslocoService } from '@ngneat/transloco';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import {
  HdBodygraphCenter,
  HdBodygraphConfig,
  HdBodygraphPlanetaryActivation,
} from '../hd-bodygraph.models';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { HdChartPanelViewMode } from '../hd-chart-panel-view-mode';
import {
  HdChartEnvironmentMatch,
  HdChartEnvironmentMatchLocation,
  HdChartEnvironmentSearchKind,
  HdChartMatchScope,
  HdChartMatchesService,
} from '../../library/hd-chart-matches.service';
import { HdChartMatchListComponent } from '../../library/hd-chart-match-list.component';
import { HdChartMatchToggleComponent } from '../../library/hd-chart-match-toggle.component';
import {
  deriveBasicProperties,
  deriveIncarnationCross,
  deriveLifeCross,
  deriveProfile,
  formatHdIncarnationCrossValue,
  formatHdLifeCrossValue,
  formatHdProfileValue,
} from './hd-chart-properties.util';
import {
  HdActivationRow,
  HdChartBasics,
  HdEnergyType,
  HdPrimaryConditioningKind,
  HdUndefinedCenter,
} from './hd-chart-properties.types';
import {
  HD_ACTIVATION_PLANET_ORDER,
  HD_BASIC_PROPERTY_TILE_ICONS,
  HdBasicPropertyKey,
} from './hd-chart-properties.constants';
import { HdCenterMiniCardComponent } from './hd-center-mini-card.component';
import {
  HdPlanetActivationVm,
  HdPlanetMiniCardComponent,
  HdPlanetMiniCardKeynoteLabels,
} from './hd-planet-mini-card.component';
import {
  HdChannelListChannelInfoRequest,
  HdChannelListItemComponent,
} from '../channel-list/hd-channel-list-item.component';
import { HD_BODYGRAPH_CIRCUIT_THEME_ICONS } from '../hd-bodygraph-circuit-metadata';
import { HdBodygraphIconKey, getHdGateIconLayout } from '../hd-bodygraph-icons';
import { withHdFearIconDetail } from '../gate-info/line/hd-gate-line.view-model';
import { HdBridgeGateStyle } from '../hd-chart-controls-options';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import { HD_CHIRON_DISPLAY_CONFIG, getHdPlanetDisplayConfig } from '../planet-grid/hd-planet-display-config';
import { HdChironClickEvent, HdChironPosition, HdChironSide } from '../hd-chiron.models';
import { HdMiniCardActivation } from './hd-mini-card-activation';
import { HdTypeInfoTopic } from '../chart-basics-info/hd-chart-basics-info-metadata';
import {
  HdChartGlobalSettingsV1,
  loadHdChartGlobalSettings,
  saveHdChartGlobalSettings,
} from '../chart-tabs/hd-chart-global-settings';
import { HdPropertyTileComponent } from './hd-property-tile.component';
import {
  HdPrimaryConditioningValueComponent,
  HdPrimaryConditioningVm,
  buildHdPrimaryConditioningVm,
} from './hd-primary-conditioning-value.component';
import { HdActivePaletteService } from '../hd-active-palette.service';

/** Accentkleuren voor de twee activatie-kolommen (icoon + planeetnaam). */
const HD_DESIGN_ACCENT_COLOR = '#c0392b';
const HD_PERSONALITY_ACCENT_COLOR = '#1a1a1a';

type HdActivationSide = 'personality' | 'design' | 'both';

/** Weergave van de sectie Eigenschappen: tegels (default) of de klassieke lijst. */
type HdBasicPropertiesView = HdChartGlobalSettingsV1['basicPropertiesView'];

interface TypeVm { pure: string; modifier: string; base: string }
interface RowVm {
  key: HdBasicPropertyKey;
  icon: string;
  /**
   * Tegelweergave: over beide kolommen. De rijen met een omgeving-matchlijst
   * (profiel, kruisen): een uitgeklapte lijst in een halve tegel is te krap.
   */
  tileWide: boolean;
  name: string;
  value: string;
  /** Aanwezig op de rijen met een omgeving-matchlijst (profiel/kruis/levenskruis). */
  matchKind?: HdChartEnvironmentSearchKind;
  matchTooltip?: string;
  /** Aanwezig op de rijen met een info-icoon; bepaalt welk paneel de klik opent. */
  infoTooltip?: string;
  infoKind?: 'profile' | 'incarnationCross' | 'lifeCross' | 'strategy' | 'signature' | 'notSelf' | 'authority' | 'definition';
  /** Poorten die bij de rij horen (kruisen); hover licht ze samen op in de chart. */
  hoverGates?: readonly number[];
}
interface ActivationVm extends HdPlanetActivationVm {
  /**
   * CSS-`order` voor de telefoon-weergave van "Beide": één kolom met de planeten
   * om beurten (persoonlijkheid zon, design zon, persoonlijkheid aarde, …). Volgt
   * de plaats van de planeet in {@link HD_ACTIVATION_PLANET_ORDER}, niet de index:
   * ontbreekt een planeet aan één kant, dan blijven de paren toch kloppen. Chiron
   * komt na alle planeten. In de twee kolommen van de desktop stijgt de waarde per
   * kolom gewoon mee en verandert ze niets.
   */
  interleaveOrder: number;
}

/** Wat elk activatie-blokje van één opbouw deelt: vertaalde labels, één keer per taal opgezocht. */
interface ActivationVmShared {
  lang: string;
  t: (key: string) => string;
  keynoteLabels: HdPlanetMiniCardKeynoteLabels;
  iconLabels: Readonly<Record<HdBodygraphIconKey, string>>;
  centerLabels: Readonly<Record<HdBodygraphCenter, string>>;
}
interface CircuitryVm { name: string; keynote: string; themeIcon: string }

/** Teksten voor de omgeving-matchlijsten (fase B), in de actieve content-taal. */
interface EnvironmentVm {
  disclaimer: string;
  loading: string;
  empty: string;
  error: string;
  strongTooltip: string;
  locations: Record<HdChartEnvironmentMatchLocation, string>;
}

interface BasicPropertiesVm {
  labels: Record<string, string>;
  type: TypeVm;
  rows: RowVm[];
  circuitry: CircuitryVm[];
  primaryConditioning: HdPrimaryConditioningVm;
  definedCenters: HdBodygraphCenter[];
  undefinedCenters: HdUndefinedCenter[];
  /** Canonieke kanaal-keys; het item-component doet zelf de vertaling/opmaak. */
  definedChannels: string[];
  activations: { personality: ActivationVm[]; design: ActivationVm[] };
  /** Chiron per zijde, onderaan de kolommen in een gedempte stijl: géén activatie. */
  chiron: { personality: ActivationVm | null; design: ActivationVm | null };
  environment: EnvironmentVm;
}

interface EnvironmentMatchListState {
  readonly status: 'idle' | 'loading' | 'loaded' | 'error';
  readonly items: readonly HdChartEnvironmentMatch[];
}

const ENVIRONMENT_MATCH_IDLE: EnvironmentMatchListState = { status: 'idle', items: [] };

const ENVIRONMENT_MATCH_IDLE_STATES: Readonly<Record<HdChartEnvironmentSearchKind, EnvironmentMatchListState>> = {
  crossToNodes: ENVIRONMENT_MATCH_IDLE,
  profileToNodeLines: ENVIRONMENT_MATCH_IDLE,
  lifeCrossToCross: ENVIRONMENT_MATCH_IDLE,
};

const ENVIRONMENT_LOCATION_KEYS: readonly HdChartEnvironmentMatchLocation[] = [
  'nodesPersonality', 'nodesDesign', 'crossPersonality', 'crossDesign', 'profilePersonality', 'profileDesign',
];

const P = 'humanDesign.properties';

/** Plaats van een activatie in de om-en-om-lijst: persoonlijkheid vóór design per planeet. */
function interleaveOrder(planetIndex: number, side: 'personality' | 'design'): number {
  return planetIndex * 2 + (side === 'design' ? 1 : 0);
}

@Component({
  selector: 'hd-basic-properties',
  standalone: true,
  templateUrl: './hd-basic-properties.component.html',
  styleUrl: './hd-basic-properties.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(mouseleave)': 'onPanelLeave()',
  },
  imports: [
    NgTemplateOutlet,
    ESegmentedToggleComponent,
    HdCenterMiniCardComponent,
    HdPlanetMiniCardComponent,
    HdChannelListItemComponent,
    HdPropertyTileComponent,
    HdPrimaryConditioningValueComponent,
    HdChartMatchListComponent,
    HdChartMatchToggleComponent,
  ],
})
export class HdBasicPropertiesComponent {
  readonly configuration = input.required<HdBodygraphConfig>();
  readonly planetaryActivations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly advancedBridgeCalculation = input(true);
  readonly bridgeGateStyle = input<HdBridgeGateStyle>('badge');
  /** Omgeving-matchlijsten alleen op persoons-charts (niet bij cycle-presets). */
  readonly environmentMatchesEnabled = input(false);
  readonly matchScope = input<HdChartMatchScope>('library');
  /** Eigen chart-id: uitgesloten van de matchlijsten en bron van de relatie-scope. */
  readonly savedChartId = input<string | null>(null);
  /**
   * Chiron-posities (design/personality) van deze chart: onderaan de
   * activatie-kolommen in een subtielere stijl. Geen activaties — ze tellen
   * nergens mee (type, definitie, kanalen, statistieken).
   */
  readonly chironPositions = input<readonly HdChironPosition[]>([]);
  /**
   * Center- en activatie-kaartjes gekoppeld aan de chart-hover (highlight in beide
   * richtingen, Ctrl/Cmd+klik bevriest). Instellingenpaneel-setting, zwaar, default
   * uit: dan enkel een eenvoudige hover en klik → info.
   */
  readonly chartLinked = input(false, { transform: booleanAttribute });
  /** Weergavestand (spec 2026-10-20-chart-share §4.5): gast → elke klik meldt "account nodig". */
  readonly viewMode = input<HdChartPanelViewMode>('owner');

  /** Gaststand: een klik die normaal een paneel opent; de host meldt dat een account nodig is. */
  readonly accountRequired = output<void>();
  /** Opent het gate-info paneel — zelfde gedrag als een klik op een poort in de chart/planeetgrid. */
  readonly gateInfoRequested = output<{ gate: number; origin: { x: number; y: number } }>();
  /** Opent het kanaal-info paneel — zelfde gedrag als een klik op het kanaal in de chart. */
  readonly channelInfoRequested = output<HdChannelListChannelInfoRequest>();
  /** Klik op een match-item: open die chart. */
  readonly chartOpenRequested = output<string>();
  /** Info-icoon op de levenskruis-rij: opent het nodale-omgevingen-paneel. */
  readonly lifeCrossInfoRequested = output<{ origin: { x: number; y: number } }>();
  /** Info-icoon op de profiel-rij: opent het profiel & rollen-paneel. */
  readonly profileInfoRequested = output<{ origin: { x: number; y: number } }>();
  /** Info-icoon op de kruis-rij: opent het incarnatiekruis-info-paneel. */
  readonly incarnationCrossInfoRequested = output<{ origin: { x: number; y: number } }>();
  /** Info-icoon naast de sectietitel Eigenschappen: opent het basics-paneel. */
  readonly basicsInfoRequested = output<{ origin: { x: number; y: number } }>();
  /** Info-icoon op de type-, strategie-, signatuur- of niet-zelf-rij: opent het paneel van dat onderwerp. */
  readonly typeInfoRequested = output<{ topic: HdTypeInfoTopic; origin: { x: number; y: number } }>();
  /** Info-icoon op de autoriteit-rij: opent het autoriteit-paneel. */
  readonly authorityInfoRequested = output<{ origin: { x: number; y: number } }>();
  /** Info-icoon op de definitie-rij: opent het definitie-paneel. */
  readonly definitionInfoRequested = output<{ origin: { x: number; y: number } }>();
  /** Info-icoon naast "Primaire Conditionering": opent het conditionering-info-paneel. */
  readonly conditioningInfoRequested = output<{ origin: { x: number; y: number } }>();
  /** Klik op een center-kaartje: opent de center-info, zoals een klik op het center in de chart. */
  readonly centerInfoRequested = output<{ center: HdBodygraphCenter; origin: { x: number; y: number } }>();
  /** Klik op een Chiron-blokje: de gate-info van haar poort, zoals een klik op Chiron in de planeetgrid. */
  readonly chironClicked = output<HdChironClickEvent>();

  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly transloco = inject(TranslocoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly centerColors = inject(HdActivePaletteService);
  private readonly hoverHighlight = inject(HdChartHoverHighlightService, { optional: true });
  private readonly chartMatches = inject(HdChartMatchesService);

  protected readonly activeSide = signal<HdActivationSide>('both');

  /** Iconen in de activatie-blokjes openen hun paneel; in de gaststand vallen ze door naar het blokje (account nodig). */
  protected readonly iconsClickable = computed(() => this.viewMode() !== 'guest');

  /** Gast: niets openen, wel melden. True = de aanroeper stopt. */
  private guestBlocked(event?: Event): boolean {
    if (this.viewMode() !== 'guest') return false;
    event?.preventDefault();
    event?.stopPropagation();
    this.accountRequired.emit();
    return true;
  }

  /** Tegels of lijst voor de sectie Eigenschappen; gebruikerssetting, bewaard bij wijziging. */
  protected readonly propertiesView = signal<HdBasicPropertiesView>(loadHdChartGlobalSettings().basicPropertiesView);

  /** Icoonknoppen van de weergave-toggle Tegels/Lijst in de sectietitel. */
  protected readonly viewOptions = computed<readonly ESegmentedToggleOption<HdBasicPropertiesView>[]>(() => {
    const labels = this.vm().labels;
    return [
      { value: 'tiles', label: labels.viewTiles, icon: 'grid-2' },
      { value: 'list', label: labels.viewList, icon: 'list' },
    ];
  });

  protected readonly tileIcons = HD_BASIC_PROPERTY_TILE_ICONS;

  /** Knoppen van de activatie-toggle Beide/Design/Persoonlijkheid (gedeelde e-segmented-toggle). */
  protected readonly sideOptions = computed<readonly ESegmentedToggleOption<HdActivationSide>[]>(() => {
    const labels = this.vm().labels;
    return [
      { value: 'both', label: labels.both },
      { value: 'design', label: labels.design },
      { value: 'personality', label: labels.personality },
    ];
  });

  /** Uitgeklapte omgeving-matchlijsten + laad-status per zoektocht (lazy geladen). */
  protected readonly expandedEnvironmentMatches = signal<ReadonlySet<HdChartEnvironmentSearchKind>>(new Set());
  protected readonly environmentMatchStates =
    signal<Readonly<Record<HdChartEnvironmentSearchKind, EnvironmentMatchListState>>>(ENVIRONMENT_MATCH_IDLE_STATES);

  /** Bron-eigenschappen van de actieve chart voor de omgeving-zoektochten. */
  private readonly environmentMatchSources = computed(() => {
    const activations = this.planetaryActivations();
    return {
      profile: deriveProfile(activations),
      incarnationCross: deriveIncarnationCross(activations),
      lifeCross: deriveLifeCross(activations),
    };
  });

  constructor() {
    // Andere chart of andere match-scope → cache en uitklap-status resetten,
    // zodat de volgende uitklap verse resultaten laadt.
    effect(() => {
      this.planetaryActivations();
      this.matchScope();
      this.expandedEnvironmentMatches.set(new Set());
      this.environmentMatchStates.set(ENVIRONMENT_MATCH_IDLE_STATES);
    });
  }

  protected readonly designAccentColor = HD_DESIGN_ACCENT_COLOR;
  protected readonly personalityAccentColor = HD_PERSONALITY_ACCENT_COLOR;

  protected readonly vm = computed<BasicPropertiesVm>(() => this.buildVm());

  /** Weergave van de eigenschappen wisselen en de keuze bewaren in de gebruikerssettings. */
  protected setPropertiesView(view: HdBasicPropertiesView): void {
    this.propertiesView.set(view);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), basicPropertiesView: view });
  }

  /**
   * Container-leave-vangnet (docs/human-design/hd-chart-hover-highlight.md §3.5), zoals
   * bij chart en planeetgrid: het paneel publiceert hovers (kaartjes, kruisen,
   * brugpoorten, centernamen); een gedropte `mouseleave` mag geen highlight laten hangen.
   */
  protected onPanelLeave(): void {
    this.hoverHighlight?.clearForContainer(['planet-grid-block', 'chart-center', 'chart-gate']);
  }

  /** Hover op een brugpoort → highlight de poort in de chart/planeetgrid. */
  protected onBridgeGateEnter(gate: number): void {
    this.hoverHighlight?.setGateHover(gate, 'chart-gate');
  }

  protected onBridgeGateLeave(): void {
    this.hoverHighlight?.setGateHover(null, 'chart-gate');
  }

  /** Klik op een brugpoort → opent het gate-info paneel (zoals een klik in de chart). */
  protected onBridgeGateClick(gate: number, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.gateInfoRequested.emit({ gate, origin: { x: event.clientX, y: event.clientY } });
  }

  /**
   * Hover op een kruis-waarde → licht alle poorten van dat kruis tegelijk op
   * in de chart/planeetgrid (zelfde highlight als een losse poort-hover).
   */
  protected onRowGatesEnter(row: RowVm): void {
    if (!row.hoverGates) return;
    this.hoverHighlight?.setGatesHover([...row.hoverGates], 'chart-gate');
  }

  protected onRowGatesLeave(row: RowVm): void {
    if (!row.hoverGates) return;
    this.hoverHighlight?.setGatesHover(null, 'chart-gate');
  }

  /**
   * Hover op een center-naam (primaire conditionering) → highlight het center in
   * de chart/planeetgrid, zoals een center-hover in de chart. De center-kaartjes
   * doen dit zelf (`hd-center-mini-card` met `interactive`).
   */
  protected onCenterHoverEnter(center: HdBodygraphCenter): void {
    this.hoverHighlight?.setCenterHover(center);
  }

  protected onCenterHoverLeave(): void {
    this.hoverHighlight?.setCenterHover(null);
  }

  protected isEnvironmentExpanded(kind: HdChartEnvironmentSearchKind): boolean {
    return this.expandedEnvironmentMatches().has(kind);
  }

  /** Tegelweergave: staat de matchlijst van deze rij open (toggle-staat + footer onder de waarde)? */
  protected isRowMatchListOpen(row: RowVm): boolean {
    return this.environmentMatchesEnabled() && !!row.matchKind && this.isEnvironmentExpanded(row.matchKind);
  }

  protected environmentState(kind: HdChartEnvironmentSearchKind): EnvironmentMatchListState {
    return this.environmentMatchStates()[kind];
  }

  /** Klik op het match-icoon: uitklappen (met lazy load bij de eerste keer) of inklappen. */
  protected toggleEnvironmentMatches(kind: HdChartEnvironmentSearchKind): void {
    if (this.guestBlocked()) return;
    const willExpand = !this.expandedEnvironmentMatches().has(kind);
    this.expandedEnvironmentMatches.update(expanded => {
      const next = new Set(expanded);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
    if (willExpand && this.environmentMatchStates()[kind].status === 'idle') {
      void this.loadEnvironmentMatches(kind);
    }
  }

  protected openMatch(item: PlatformChartListItemDto): void {
    if (this.guestBlocked()) return;
    if (item.id) this.chartOpenRequested.emit(item.id);
  }

  /** Klik op een rij-info-icoon → open het bijhorende paneel. */
  protected onRowInfoClick(row: RowVm, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    const origin = { x: event.clientX, y: event.clientY };
    switch (row.infoKind) {
      case 'profile':
        this.profileInfoRequested.emit({ origin });
        return;
      case 'strategy':
        this.typeInfoRequested.emit({ topic: 'strategy', origin });
        return;
      case 'signature':
      case 'notSelf':
        this.typeInfoRequested.emit({ topic: 'signature', origin });
        return;
      case 'authority':
        this.authorityInfoRequested.emit({ origin });
        return;
      case 'definition':
        this.definitionInfoRequested.emit({ origin });
        return;
      case 'incarnationCross':
        this.incarnationCrossInfoRequested.emit({ origin });
        return;
      default:
        this.lifeCrossInfoRequested.emit({ origin });
    }
  }

  /** Info-icoon naast de sectietitel Eigenschappen → basics-paneel. */
  protected onBasicsInfoClick(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.basicsInfoRequested.emit({ origin: { x: event.clientX, y: event.clientY } });
  }

  /** Info-icoon op de type-rij → type-paneel. */
  protected onTypeInfoClick(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.typeInfoRequested.emit({ topic: 'type', origin: { x: event.clientX, y: event.clientY } });
  }

  /** Info-icoon naast "Primaire Conditionering" → conditionering-info-paneel. */
  protected onConditioningInfoClick(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.conditioningInfoRequested.emit({ origin: { x: event.clientX, y: event.clientY } });
  }

  /** Klik op een center-kaartje (gedefinieerd of ongedefinieerd) → center-info. */
  protected onCenterCardActivated(center: HdBodygraphCenter, activation: HdMiniCardActivation): void {
    if (this.guestBlocked()) return;
    this.centerInfoRequested.emit({ center, origin: activation.origin });
  }

  /** Klik op een activatie-blokje → gate-info van die poort, zoals een poortklik in de chart. */
  protected onActivationCardActivated(activation: ActivationVm, card: HdMiniCardActivation): void {
    if (this.guestBlocked()) return;
    this.gateInfoRequested.emit({ gate: activation.gate, origin: card.origin });
  }

  /** Klik op een Chiron-blokje → gate-info met de Chiron-positie (geen activatie). */
  protected onChironCardActivated(side: HdChironSide, card: HdMiniCardActivation): void {
    if (this.guestBlocked()) return;
    this.chironClicked.emit({ side, origin: card.origin });
  }

  private async loadEnvironmentMatches(kind: HdChartEnvironmentSearchKind): Promise<void> {
    const sources = this.environmentMatchSources();
    const savedChartId = this.savedChartId();
    this.setEnvironmentState(kind, { status: 'loading', items: [] });
    try {
      const items = await this.chartMatches.findEnvironmentMatches({
        kind,
        scope: this.matchScope(),
        sourceChartId: savedChartId,
        excludeChartIds: savedChartId ? [savedChartId] : [],
        incarnationCross: sources.incarnationCross,
        profile: sources.profile,
        lifeCross: sources.lifeCross,
      });
      this.setEnvironmentState(kind, { status: 'loaded', items });
    } catch {
      this.setEnvironmentState(kind, { status: 'error', items: [] });
    }
  }

  private setEnvironmentState(kind: HdChartEnvironmentSearchKind, state: EnvironmentMatchListState): void {
    this.environmentMatchStates.update(states => ({ ...states, [kind]: state }));
  }

  private buildVm(): BasicPropertiesVm {
    const lang = this.contentLang.effectiveLang();
    const renderState = this.renderStateFactory.create(this.configuration());
    const data = deriveBasicProperties(
      renderState,
      this.planetaryActivations(),
      this.advancedBridgeCalculation(),
    );
    const t = (key: string) => this.transloco.translate(`${P}.${key}`, undefined, lang);

    const row =(key: HdBasicPropertyKey, fields: Omit<RowVm, 'key' | 'icon' | 'tileWide'>, tileWide = false): RowVm =>
      ({ key, icon: HD_BASIC_PROPERTY_TILE_ICONS[key], tileWide, ...fields });

    const rows: RowVm[] = [
      row('strategy', {
        name: t('strategy'), value: t(`strategyValues.${data.strategyKey}`),
        infoTooltip: t('strategyInfoTooltip'), infoKind: 'strategy',
      }),
      row('signature', {
        name: t('signature'), value: t(`signatureValues.${data.signatureKey}`),
        infoTooltip: t('signatureInfoTooltip'), infoKind: 'signature',
      }),
      row('notSelf', {
        name: t('notSelfTheme'), value: t(`notSelfValues.${data.notSelfKey}`),
        infoTooltip: t('notSelfInfoTooltip'), infoKind: 'notSelf',
      }),
      row('authority', {
        name: t('authority'), value: t(`authorityValues.${data.authorityKey}`),
        infoTooltip: t('authorityInfoTooltip'), infoKind: 'authority',
      }),
      row('definition', {
        name: t('definition'), value: t(`definitionValues.${data.definitionKey}`),
        infoTooltip: t('definitionInfoTooltip'), infoKind: 'definition',
      }),
      row('profile', {
        name: t('profile'), value: formatHdProfileValue(data.profile, t),
        matchKind: 'profileToNodeLines', matchTooltip: t('environmentMatches.profileTooltip'),
        infoTooltip: t('profileInfoTooltip'), infoKind: 'profile',
      }, true),
      row('incarnationCross', {
        name: t('incarnationCross'), value: formatHdIncarnationCrossValue(data.incarnationCross, t, lang, this.gateInfo),
        matchKind: 'crossToNodes', matchTooltip: t('environmentMatches.crossTooltip'),
        infoTooltip: t('incarnationCrossInfoTooltip'), infoKind: 'incarnationCross',
        hoverGates: data.incarnationCross?.gates,
      }, true),
    ];
    // Levenskruis (nodes-variant van het incarnatiekruis) direct onder het kruis;
    // geen rij wanneer de node-activaties (nog) ontbreken.
    const lifeCross = deriveLifeCross(this.planetaryActivations());
    if (lifeCross) {
      rows.push(row('lifeCross', {
        name: t('lifeCross'), value: formatHdLifeCrossValue(lifeCross),
        matchKind: 'lifeCrossToCross', matchTooltip: t('environmentMatches.lifeCrossTooltip'),
        infoTooltip: t('lifeCrossInfoTooltip'), infoKind: 'lifeCross',
        hoverGates: [
          lifeCross.personality.southGate, lifeCross.personality.northGate,
          lifeCross.design.southGate, lifeCross.design.northGate,
        ],
      }, true));
    }

    const activationShared: ActivationVmShared = {
      lang,
      t,
      keynoteLabels: {
        planet: t('activationKeynotes.planet'),
        gate: t('activationKeynotes.gate'),
        line: t('activationKeynotes.line'),
        planetTooltip: t('activationKeynotes.planetTooltip'),
        gateTooltip: t('activationKeynotes.gateTooltip'),
        lineTooltip: t('activationKeynotes.lineTooltip'),
      },
      iconLabels: this.gateInfo.iconLabelsIn(lang),
      centerLabels: this.gateInfo.centerLabelsIn(lang),
    };

    const environmentLocations = {} as Record<HdChartEnvironmentMatchLocation, string>;
    for (const location of ENVIRONMENT_LOCATION_KEYS) {
      environmentLocations[location] = t(`environmentMatches.locations.${location}`);
    }

    return {
      labels: {
        properties: t('properties'),
        basicsInfoTooltip: t('basicsInfoTooltip'),
        typeInfoTooltip: t('typeInfoTooltip'),
        definedCenters: t('definedCenters'),
        undefinedCenters: t('undefinedCenters'),
        definedChannels: t('definedChannels'),
        activations: t('activations'),
        chironNoteDesign: t('chironNoteWork'),
        chironNotePersonality: t('chironNoteWound'),
        type: t('type'),
        circuitry: t('circuitry'),
        personality: t('tabPersonality'),
        design: t('tabDesign'),
        both: t('tabBoth'),
        viewTiles: t('viewTiles'),
        viewList: t('viewList'),
        viewToggle: t('viewToggle'),
      },
      type: this.typeVm(data.type, t),
      rows,
      circuitry: data.circuitry.map(c => ({
        name: t(`circuitryNames.${c.nameKey}`),
        keynote: t(`circuitryKeynotes.${c.themeKey}`),
        themeIcon: HD_BODYGRAPH_CIRCUIT_THEME_ICONS[c.themeKey],
      })),
      primaryConditioning: buildHdPrimaryConditioningVm(
        data.primaryConditioning, t, center => this.centerColors.color(center), this.bridgeGateStyle(),
      ),
      definedCenters: data.definedCenters,
      undefinedCenters: data.undefinedCenters,
      definedChannels: [...data.definedChannels],
      activations: {
        personality: data.activations.personality.map(row => this.activationVm(row, activationShared)),
        design: data.activations.design.map(row => this.activationVm(row, activationShared)),
      },
      chiron: {
        personality: this.chironVm('personality', activationShared),
        design: this.chironVm('design', activationShared),
      },
      environment: {
        disclaimer: t('environmentMatches.disclaimer'),
        loading: t('environmentMatches.loading'),
        empty: t('environmentMatches.empty'),
        error: t('environmentMatches.error'),
        strongTooltip: t('environmentMatches.strongTooltip'),
        locations: environmentLocations,
      },
    };
  }

  private typeVm(type: HdEnergyType, t: (k: string) => string): TypeVm {
    return {
      pure: type.pure ? t('purePrefix') : '',
      modifier: type.modifier ? t(`typeValues.${type.modifier}`) : '',
      base: t(`typeValues.${type.base}`),
    };
  }

  /** Chiron als activatie-kaartje (zelfde opbouw), enkel voor de weergave. */
  private chironVm(side: 'personality' | 'design', shared: ActivationVmShared): ActivationVm | null {
    const position = this.chironPositions().find(candidate => candidate.side === side);
    if (position == null) return null;
    const vm = this.activationVm(
      { side, planetKey: 'chiron', planetId: HD_CHIRON_DISPLAY_CONFIG.planetId, gate: position.gate, line: position.line },
      shared,
    );
    return { ...vm, interleaveOrder: interleaveOrder(HD_ACTIVATION_PLANET_ORDER.length, side) };
  }

  private activationVm(row: HdActivationRow, shared: ActivationVmShared): ActivationVm {
    const { lang, t } = shared;
    const gate = this.gateInfo.gateInfoIn(row.gate, lang);
    const planet = this.planetInfo.planetInfoIn(row.planetKey, lang);
    const circuitTheme = gate.mainCircuit ? (gate.mainCircuit.theme ?? 'bekrachtiging') : null;
    // Zelfde iconen en tooltips als de kop van de gate-info (angst-tooltip met de angst van de poort).
    const iconKeys = getHdGateIconLayout(row.gate).flat();
    const fearDetail = iconKeys.some(key => key.startsWith('fear-'))
      ? this.gateInfo.fearIconDetailIn(row.gate, lang)
      : '';
    return {
      interleaveOrder: interleaveOrder(HD_ACTIVATION_PLANET_ORDER.indexOf(row.planetKey), row.side),
      icon: getHdPlanetDisplayConfig(row.planetKey).astronomiconChar,
      planet: planet?.name ?? row.planetKey,
      planetKeynote: planet?.subtitle ?? '',
      gate: row.gate,
      gateLine: `${row.gate}.${row.line}`,
      firstKeynote: gate.translations.keynote1,
      secondKeynote: gate.translations.keynote2,
      lineNumber: row.line,
      lineKeynote: gate.translations.lines[row.line - 1]?.keynote ?? '',
      circuitKeynote: circuitTheme ? t(`circuitryKeynotes.${circuitTheme}`) : '',
      circuitIcon: circuitTheme ? HD_BODYGRAPH_CIRCUIT_THEME_ICONS[circuitTheme] : '',
      attributeIcons: iconKeys.map(key => ({
        key,
        label: withHdFearIconDetail(key, shared.iconLabels[key] ?? key, fearDetail),
      })),
      center: {
        iconHtml: this.centerColors.iconHtml(gate.center),
        tooltip: this.transloco.translate(
          `${P}.activationKeynotes.centerTooltip`,
          { center: shared.centerLabels[gate.center] ?? gate.center },
          lang,
        ),
      },
      keynoteLabels: shared.keynoteLabels,
    };
  }
}
